import { matchPath } from 'react-router-dom'

import { getPluginSlots } from '@/plugin/slots'

export type PublicShareRouteMatch =
  | { kind: 'interview'; token: string }
  | { kind: 'plugin'; index: number }
  | { kind: 'apply-complete'; token: string }
  | { kind: 'apply'; jobId: string }
  | { kind: 'job'; slug: string }
  | { kind: 'organisation'; slug: string }
  | { kind: 'profile'; slug: string }

/**
 * Interview stays first. Plugin public routes win over `/jobs/:slug`,
 * `/apply/:jobId`, and `/apply/complete/:token`. Organisation and profile
 * pages stay after those three.
 */
export function matchPublicShareRoute(
  pathname: string,
  pluginRoutes: readonly { path: string }[],
): PublicShareRouteMatch | null {
  const interview = matchPath('/interview/:token', pathname)
  if (interview?.params.token) {
    return { kind: 'interview', token: interview.params.token }
  }

  for (let index = 0; index < pluginRoutes.length; index += 1) {
    const route = pluginRoutes[index]
    if (route && matchPath(route.path, pathname)) {
      return { kind: 'plugin', index }
    }
  }

  const applyComplete = matchPath('/apply/complete/:token', pathname)
  if (applyComplete?.params.token) {
    return { kind: 'apply-complete', token: applyComplete.params.token }
  }

  const apply = matchPath('/apply/:jobId', pathname)
  if (apply?.params.jobId) {
    return { kind: 'apply', jobId: apply.params.jobId }
  }

  const job = matchPath('/jobs/:slug', pathname)
  if (job?.params.slug) {
    return { kind: 'job', slug: job.params.slug }
  }

  const organisation = matchPath('/o/:slug', pathname)
  if (organisation?.params.slug) {
    return { kind: 'organisation', slug: organisation.params.slug }
  }

  const profile = matchPath('/p/:slug', pathname)
  if (profile?.params.slug) {
    return { kind: 'profile', slug: profile.params.slug }
  }

  return null
}

/** Paths that render outside the logged-in agent/sidebar shell. */
export function isPublicSharePath(pathname: string): boolean {
  const path = pathname || '/'
  if (
    path.startsWith('/jobs/') ||
    path.startsWith('/o/') ||
    path.startsWith('/p/') ||
    path.startsWith('/apply') ||
    path.startsWith('/share/') ||
    path.startsWith('/interview/')
  ) {
    return true
  }
  return getPluginSlots().publicShareRoutes.some((route) => Boolean(matchPath(route.path, path)))
}
